import ActionButton from "../shared/ActionButton";
import ActionButtonGroup, { ActionButtonConfig } from "../shared/ActionButtonGroup";
import { DuplicateGroup, useDuplicateAnalysis } from "../../hooks/useDuplicateAnalysis";
import { useParticipantsContext } from "../../contexts/ParticipantsContext";
import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

interface DuplicatesManagerProps {
  onClose: () => void;
}

export default function DuplicatesManager({ onClose }: DuplicatesManagerProps) {
  const { groups } = useDuplicateAnalysis();
  const { handleParticipantDelete, handleParticipantUpdate } = useParticipantsContext();
  const [selectedPerGroup, setSelectedPerGroup] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState<{
    groupSig: string;
    keepId: string;
    toRemoveIds: string[];
    summary: string;
  } | null>(null);

  if (groups.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">Brak duplikatów</Typography>
          <Typography variant="body2" color="text.secondary">
            Nie wykryto potencjalnych duplikatów.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <ActionButton onClick={onClose} variant="outlined">
              Zamknij
            </ActionButton>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        {groups.map((group: DuplicateGroup, idx) => (
          <Card key={group.signature + idx}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{group.signature.replace("__", " ")}</Typography>
                <Chip
                  label={
                    group.reason === "BIRTHDATE_CONFLICT"
                      ? "Konflikt daty"
                      : group.reason === "SKRUTINER_ID"
                      ? "ID konflikt"
                      : "Możliwy duplikat"
                  }
                  color={group.reason === "BIRTHDATE_CONFLICT" ? "warning" : "info"}
                  sx={{ color: group.reason === "BIRTHDATE_CONFLICT" ? "default" : "white" }}
                />
              </Stack>
              <Divider sx={{ my: 1 }} />
              <RadioGroup
                value={selectedPerGroup[group.signature] ?? group.participants[0]?.id}
                onChange={(e) =>
                  setSelectedPerGroup((prev) => ({ ...prev, [group.signature]: e.target.value }))
                }
              >
                <Stack spacing={1}>
                  {group.participants.map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <FormControlLabel
                        value={p.id as string}
                        control={<Radio />}
                        label={`${p.firstName} ${p.lastName}`}
                      />
                      <Typography color="text.secondary">
                        {new Date(p.birthDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </RadioGroup>
              <ActionButtonGroup
                buttons={[
                  {
                    id: "merge",
                    label: "Scal (zachowaj zaznaczony)",
                    variant: "contained",
                    color: "primary",
                    onClick: () => {
                      if (group.participants.length <= 1) return;
                      const keepId = (selectedPerGroup[group.signature] ??
                        group.participants[0]?.id) as string;
                      const toRemove = group.participants.filter((p) => p.id !== keepId);
                      const summary = toRemove
                        .map(
                          (p) =>
                            `${p.firstName} ${p.lastName} (${new Date(
                              p.birthDate
                            ).toLocaleDateString()})`
                        )
                        .join(", ");
                      setConfirmData({
                        groupSig: group.signature,
                        keepId,
                        toRemoveIds: toRemove.map((p) => p.id as string),
                        summary,
                      });
                      setConfirmOpen(true);
                    },
                  },
                  {
                    id: "ignore",
                    label: "To nie duplikat",
                    variant: "outlined",
                    onClick: async () => {
                      await Promise.all(
                        group.participants.map((p) =>
                          handleParticipantUpdate(p.id as string, { duplicateIgnored: true })
                        )
                      );
                    },
                  },
                ]}
                spacing={1}
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        ))}
        <Box>
          <ActionButton onClick={onClose} variant="outlined">
            Zamknij
          </ActionButton>
        </Box>
      </Stack>

      {/* Dialog potwierdzenia scalania */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Potwierdź scalanie duplikatów</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Zostanie zachowany rekord zaznaczony. Następujące rekordy zostaną oznaczone jako
            usunięte:
          </Typography>
          <Typography sx={{ mt: 1 }}>{confirmData?.summary}</Typography>
        </DialogContent>
        <DialogActions>
          <ActionButton onClick={() => setConfirmOpen(false)} variant="outlined">
            Anuluj
          </ActionButton>
          <ActionButton
            color="primary"
            variant="contained"
            onClick={async () => {
              if (!confirmData) return;
              await Promise.all(confirmData.toRemoveIds.map((id) => handleParticipantDelete(id)));
              setConfirmOpen(false);
              setConfirmData(null);
            }}
          >
            Potwierdź
          </ActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
